import { useUser } from '@clerk/clerk-react';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
	BackendClient,
	type BackendTypes,
	type GetResponse,
	GraphQLClient,
	gql,
	type MutateResponse,
	type SearchResponse,
} from '@tf/api';
import { createPublicSchemaId, objectRelationValue, profileRelationValue } from '@tf/utils';
import { getObjectUriByNodeId } from '@/core/api/legacy';
import { useAppEnv, useEndClientTenant, useOriginTenantId } from '@/core/providers';

type Profile = {
	id: string;
	kind: string;
	labels: {
		name: string;
	};
	meta: {
		'@createdAt': string;
	};
};

export type ProfileWithStatus = Profile & {
	sharingStatus: 'DRAFT' | 'SHARED';
};

type ShareJob = {
	id: string;
	meta: {
		'@createdAt': string;
		// TODO: /@with/object is unnested when selected via path filter
		object: string[];
	};
};

const applicationsListQueryOptions = () => {
	return queryOptions({
		queryKey: ['applicationsList'],
		queryFn: async (): Promise<ProfileWithStatus[]> => {
			// TODO: add pagination later, now we just fetch 100 applications - should be enough to show "all" applications for user
			const profilesData = await GraphQLClient.getInstance().request<GetResponse<Profile>>(gql`
				query AllApplications {
					get(
						rootFilter: { archetype: "profile" }
						paginationFilter: { limit: 100, offset: 0, sortByMeta: "/@createdAt", sortByDirection: DESC }
					) {
						id
						kind
						meta
						labels
					}
				}
			`);

			if (profilesData.get.length === 0) {
				// Early return to prevent error with empty filters in the next request
				return [];
			}

			const profileLinkFilters = profilesData.get.map((p) => ({
				key: '/@with/object',
				operator: 'EQ',
				value: p.id,
			}));

			const sharedProfileIds = new Set<string>();
			const shareJobs = await GraphQLClient.getInstance().request<GetResponse<ShareJob>, { profileLinkFilters: any }>(
				gql`
					query AllShareJobs($profileLinkFilters: [KeyValueFilter!]!) {
						get(rootFilter: { archetype: "job", kind: "share", meta: { OR: $profileLinkFilters } }) {
							id
							meta(path: ["/@createdAt", "/@with/object"])
						}
					}
				`,
				{ profileLinkFilters },
			);
			shareJobs.get.forEach((job) => {
				job.meta.object.forEach((id) => {
					sharedProfileIds.add(id);
				});
			});

			return profilesData.get.map((profile) => {
				return { ...profile, sharingStatus: sharedProfileIds.has(profile.id) ? 'SHARED' : 'DRAFT' };
			});
		},
	});
};

export const useApplicationsList = () => {
	return useQuery(applicationsListQueryOptions());
};

type ExtendedReview = BackendTypes.ReviewGetData & {
	graphLinkId: BackendTypes.GraphId;
	graphNodeId: BackendTypes.GraphId;
};

export type Application = {
	account: BackendTypes.Entity;
	reviews: ExtendedReview[];
};

const applicationQueryOptions = (applicationNodeId: BackendTypes.GraphId) => {
	return queryOptions({
		queryKey: ['applications', applicationNodeId],
		queryFn: async (): Promise<Application> => {
			const entityResponse = await BackendClient.getInstance().entityGet({ entityId: applicationNodeId });
			const account: BackendTypes.EntityGetData = await entityResponse.json();

			const connections = account.listConnections
				.filter((connection) => connection.isReview)
				.flatMap((connection) =>
					connection.listFromEntities.map((entity) => ({
						graphLinkId: entity.graphLinkId,
						graphNodeId: entity.graphNodeId,
					})),
				);

			const reviews = await Promise.all(
				connections.map(async (connection) => {
					const res = await BackendClient.getInstance().reviewGet({ connectionId: connection.graphLinkId });
					const review: BackendTypes.ReviewGetData = await res.json();
					return { ...review, ...connection };
				}),
			);

			reviews.sort((a, b) => {
				return a.connectionKind.localeCompare(b.connectionKind);
			});

			return { account, reviews };
		},
	});
};

export const useApplicationQuery = (applicationNodeId: BackendTypes.GraphId) => {
	return useQuery(applicationQueryOptions(applicationNodeId));
};

export const useInvalidateApplicationQueries = () => {
	const queryClient = useQueryClient();

	// Invalidate all applications without specifying ID
	// It's ok as only the active application query will be refetched
	return () => queryClient.invalidateQueries({ queryKey: ['applications'] });
};

type ShareableObject = {
	id: string;
	archetype: string;
	kind: string;
	meta: { '@version': string };
};

export const useSubmitApplicationMutation = () => {
	const appEnv = useAppEnv();
	const originTenantId = useOriginTenantId();
	const { user } = useUser();

	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			applicationNodeId,
			applicantNodeId,
			consent,
		}: {
			applicationNodeId: BackendTypes.GraphId;
			applicantNodeId: BackendTypes.GraphId;
			consent: { label: string; description: string };
		}): Promise<void> => {
			const [applicationId, applicantId] = await Promise.all([
				getObjectUriByNodeId(applicationNodeId),
				getObjectUriByNodeId(applicantNodeId),
			]);

			// Create consent before submission
			const consentRes = await GraphQLClient.getInstance().request<MutateResponse<ShareableObject>, any>(
				gql`
					mutation CreateConsent($create: [ObjectInput]) {
						mutate(create: $create) {
							id
							archetype
							kind
							meta(path: ["/@version"])
						}
					}
				`,
				{
					create: [
						{
							id: 'new://consent_1',
							schema: createPublicSchemaId({
								clusterId: appEnv.clusterId,
								schemaTag: 'types/general/consent/client_area',
							}),
							meta: {
								signer: {
									'@kind': 'USER',
									'@provider': 'CLERK',
									'@id': user?.id,
									email: user?.primaryEmailAddress?.emailAddress ?? undefined,
								},
								signed_at: new Date().toISOString(),
							},
							labels: {
								label: consent.label,
							},
							data: {
								description: consent.description,
								profile: profileRelationValue(applicationId),
							},
						},
					],
				},
			);
			const consentObject = consentRes.mutate.find((r) => r.archetype === 'consent');

			// Find all shareable objects
			const [applicationObjects, latestLivenessCheckObjects] = await Promise.all([
				GraphQLClient.getInstance().request<SearchResponse<ShareableObject>, { applicationId: string }>(
					gql`
						query GetApplicationObjects($applicationId: ID!) {
							search(rootFilter: { id: $applicationId }) {
								id
								archetype
								kind
								meta(path: ["/@version"])
							}
						}
					`,
					{ applicationId },
				),
				GraphQLClient.getInstance().request<SearchResponse<ShareableObject>, { unitObjectId: string }>(
					gql`
						query UnitLivenessCheckId($unitObjectId: JSON!) {
							search(
								rootFilter: {
									kind: "liveness_check"
									meta: { AND: [{ key: "/@with/unit", operator: EQ, value: $unitObjectId }] }
								}
								paginationFilter: {
									limit: 1
									offset: 0
									sortByMeta: "/@createdAt"
									sortByDirection: DESC
								}
							) {
								id
								archetype
								kind
								meta(path: ["/@version"])
							}
						}
					`,
					{ unitObjectId: applicantId },
				),
			]);

			const shareableArchetypes = ['profile', 'unit', 'form', 'file'];
			const objectsToShare = applicationObjects.search
				.filter((o) => shareableArchetypes.includes(o.archetype))
				.map((o) => {
					const exactObjectId = `${o.id}?${o.meta['@version']}`;
					return objectRelationValue('share_objects', exactObjectId);
				});

			if (consentObject) {
				objectsToShare.push(
					objectRelationValue('share_objects', `${consentObject.id}?${consentObject.meta['@version']}`),
				);
			}

			// If a liveness check is available, we must share it
			const livenessCheckObject = latestLivenessCheckObjects.search.find((o) => o.kind === 'liveness_check');

			if (livenessCheckObject) {
				objectsToShare.push(
					// Liveness check record
					objectRelationValue('share_objects', `${livenessCheckObject.id}?${livenessCheckObject.meta['@version']}`),
					// ...its files
					...latestLivenessCheckObjects.search
						.filter((o) => o.kind === 'record_attachment')
						.map((o) => {
							const exactObjectId = `${o.id}?${o.meta['@version']}`;
							return objectRelationValue('share_objects', exactObjectId);
						}),
				);
			}

			await GraphQLClient.getInstance().request(
				gql`
					mutation SubmitApplication($objectsToCreate: [ObjectInput]) {
						mutate(create: $objectsToCreate) {
							id
						}
					}
				`,
				{
					objectsToCreate: [
						{
							id: 'new://job_share/1',
							schema: createPublicSchemaId({
								clusterId: appEnv.clusterId,
								schemaTag: 'types/general/job/share',
							}),
							data: {
								payload: {
									object: objectRelationValue('object', applicationId),
									to_tenant_id: originTenantId,
									share_objects: objectsToShare,
								},
							},
						},
					],
				},
			);
		},
		onSuccess: () => {
			queryClient.invalidateQueries(applicationsListQueryOptions());
		},
	});
};

export const useCreateApplicationMutation = () => {
	const queryClient = useQueryClient();
	const endClientTenant = useEndClientTenant();

	return useMutation({
		mutationFn: async ({
			applicationKind,
			entitySelfSegmentKind,
			entitySelfSegmentData,
			requiredConnections,
		}: {
			applicationKind: BackendTypes.EntityKind;
			entitySelfSegmentKind: BackendTypes.SegmentKind;
			entitySelfSegmentData: Record<string, any>;
			requiredConnections?: BackendTypes.RequiredConnectionSelection[];
		}): Promise<BackendTypes.EntityConnectNewData> => {
			const res = await BackendClient.getInstance().entityConnectNew(
				{ parentEntityId: endClientTenant.nodeId, connectionKind: 'ACCOUNT', entityKind: applicationKind },
				{
					listEntitySegments: [
						{
							// @ts-expect-error No need for graphId here
							segmentIdentity: {
								segmentKind: entitySelfSegmentKind,
							},
							segment: entitySelfSegmentData,
						},
						{
							// @ts-expect-error No need for graphId here
							segmentIdentity: {
								segmentKind: 'COMMON__RM_FEEDBACK',
							},
							segment: { client_account_status: 'PROSPECT' },
						},
					],
					listConnectionSegments: [],
					requiredConnections,
				},
			);
			return res.json();
		},
		onSuccess: () => {
			queryClient.invalidateQueries(applicationsListQueryOptions());
		},
	});
};

type ApplicationSharingStatus = { status: 'DRAFT' } | { status: 'SHARED'; job: ShareJob };

const applicationSharingStatusQueryOptions = (applicationNodeId: BackendTypes.GraphId) => {
	return queryOptions({
		queryKey: ['applicationShareJob', applicationNodeId],
		queryFn: async (): Promise<ApplicationSharingStatus> => {
			const profileId = await getObjectUriByNodeId(applicationNodeId);
			const data = await GraphQLClient.getInstance().request<GetResponse<ShareJob>, { profileId: string }>(
				gql`
					query ShareJob($profileId: JSON!) {
						get(
							rootFilter: {
								archetype: "job"
								kind: "share"
								meta: { AND: [{ key: "/@with/object", operator: EQ, value: $profileId }] }
							}
							paginationFilter: { limit: 1, offset: 0, sortByMeta: "/@createdAt", sortByDirection: DESC }
						) {
							id
							meta(path: ["/@createdAt", "/@with/object"])
						}
					}
				`,
				{ profileId },
			);

			if (data.get[0]) {
				return { status: 'SHARED', job: data.get[0] };
			}

			return { status: 'DRAFT' };
		},
	});
};

export const useApplicationSharingStatus = (applicationNodeId: BackendTypes.GraphId) => {
	return useQuery(applicationSharingStatusQueryOptions(applicationNodeId));
};
